import {
  GetNextPageParamFunction,
  GetPreviousPageParamFunction,
  InfiniteData,
  QueryClient,
  QueryFunctionContext,
  UseSuspenseInfiniteQueryOptions,
  useQueryClient,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query';

import { ApiError } from '@/models/ApiErrorData.ts';
import {
  AcceptedVariables,
  CreateQueryOptions,
  QueryKey,
} from '@/queries/utils/CreateQuery';

type CreateInfiniteSuspenseQueryOptions<
  Data,
  Variables extends AcceptedVariables,
> = CreateQueryOptions<Variables, Data> & {
  getPreviousPageParam?: GetPreviousPageParamFunction<Variables, Data>;
  maxPages?: number;
};

type CreateInfiniteSuspenseQueryFunctionContext<
  Variables extends AcceptedVariables,
> = QueryFunctionContext<QueryKey, Variables> & {
  queryClient: QueryClient;
};

/**
 * Same as the options of useInfiniteSuspenseQuery but with some fields removed
 * @see UseQueryOptions
 *
 * queryKey: Already defined in the createQuery function and should not be modified
 * queryFn: Already defined in the createQuery function and should not be modified
 * networkMode: Depends on the data source and should not be modified by the UI
 * throwOnError: Use useSuspenseQuery / createSuspenseQuery instead
 * meta: Do not use if possible (untyped)
 * queryClient: Should use the default queryClient from the context (remove from omit only if you know what you are doing)
 *
 */
export type FilteredUseInfiniteSuspenseQueryOptions<
  Variables extends AcceptedVariables,
  Data,
  TransformData = Data,
> = Omit<
  UseSuspenseInfiniteQueryOptions<
    Data,
    ApiError,
    TransformData,
    Data,
    QueryKey,
    Variables
  >,
  | 'queryFn'
  | 'queryKey'
  | 'initialPageParam'
  | 'getNextPageParam'
  | 'getPreviousPageParam'
  | 'networkMode'
  | 'throwOnError'
  | 'meta'
  | 'queryClient'
>;

/**
 * Take inspiration from the doc useQuery above to generate this one:
 * Create a new infinite query
 * Returns a useInfiniteSuspenseQuery hook with an already defined behavior (the queryKey, queryFn and networkMode are already set) and some default options
 *
 * Usage:
 * - First call createInfiniteSuspenseQuery with a function, a query key and a getNextPageParams function outside the component tree. You can also add other options like retry, staleTime, etc.
 * ```ts
 * type StatsRequest = { patientId: string; from: string; to: string };
 *
 * export const useInfiniteSuspenseDataviz = createInfiniteSuspenseQuery(
 *    'infinite-dataviz',
 *    ({ patientId, from, to }) => makeQueryKey(patientId, from, to), // makeQueryKey generates a query key from potentially undefined variables @see makeQueryKey
 *    async ({ patientId, from, to }: StatsRequest) => {
 *        // Your API / Storage call here
 *        return await fetchDataviz(patientId, from, to);
 *    },
 *    (lastPage, allPages, lastPageParam) => {
 *
 */
export const createInfiniteSuspenseQuery = <
  Name extends string,
  Variables extends AcceptedVariables,
  Data,
>(
  queryName: Name,
  queryKey: (variables: Partial<Variables>) => QueryKey,
  queryFn: (
    variables: Variables,
    context: CreateInfiniteSuspenseQueryFunctionContext<Variables>,
  ) => Data | Promise<Data>,
  getNextPageParam: GetNextPageParamFunction<Variables, Data>,
  createQueryOptions?: CreateInfiniteSuspenseQueryOptions<Data, Variables>,
) => {
  const useCustomInfiniteSuspenseQuery = <TransformData = InfiniteData<Data>>(
    variables: Variables,
    options?: FilteredUseInfiniteSuspenseQueryOptions<
      Variables,
      Data,
      TransformData
    >,
  ) => {
    const queryClient = useQueryClient();
    const result = useSuspenseInfiniteQuery<
      Data,
      ApiError,
      TransformData,
      QueryKey,
      Variables
    >(
      {
        queryKey: [queryName, ...queryKey(variables)],
        queryFn: context =>
          'pageParam' in context
            ? queryFn(context.pageParam as Variables, {
                ...context,
                queryClient,
              })
            : queryFn(variables, { ...context, queryClient }),
        initialPageParam: variables,
        getNextPageParam,
        ...createQueryOptions,
        ...options,
      },
      queryClient,
    );
    if (result.status === 'error') {
      throw result.error;
    }
    return result;
  };
  useCustomInfiniteSuspenseQuery.getQueryKey = (
    variables: Partial<Variables>,
  ) => [queryName, ...queryKey(variables)];
  useCustomInfiniteSuspenseQuery.queryName = queryName;
  useCustomInfiniteSuspenseQuery.manualUpdate = (
    queryClient: QueryClient,
    variables: Variables,
    updater: (data: Data) => Data,
  ) => {
    queryClient.setQueryData(
      useCustomInfiniteSuspenseQuery.getQueryKey(variables),
      updater,
    );
  };
  useCustomInfiniteSuspenseQuery.invalidate = (
    queryClient: QueryClient,
    variables: Partial<Variables>,
  ) => {
    queryClient.invalidateQueries({
      queryKey: useCustomInfiniteSuspenseQuery.getQueryKey(variables),
    });
  };

  return useCustomInfiniteSuspenseQuery;
};
